export type Language = "fr" | "en";

export const translations = {
  // TopBar
  topbar: {
    company: { fr: "A&DW CONSULTING", en: "A&DW CONSULTING" },
    subtitle: { fr: "Études & Conseils", en: "Studies & Consulting" },
  },

  // Header nav
  nav: {
    home: { fr: "Accueil", en: "Home" },
    about: { fr: "À propos", en: "About" },
    services: { fr: "Services", en: "Services" },
    projects: { fr: "Projets", en: "Projects" },
    partners: { fr: "Partenaires", en: "Partners" },
    contact: { fr: "Contact", en: "Contact" },
  },

  // Hero
  hero: {
    badge: { fr: "Cabinet de conseil depuis 2019", en: "Consulting firm since 2019" },
    title1: { fr: "Études et conseils, au service de la digitalisation", en: "Studies and consulting, driving digitalization" },
    title2: { fr: "Stratégie et modélisation économique et financière", en: "Economic and financial strategy & modeling" },
    description: {
      fr: "A&DW Consulting accompagne les institutions et les Etats dans leurs stratégies : modélisation économique et financière, études statistiques, digitalisation.",
      en: "A&DW Consulting supports institutions and governments in their strategies: economic and financial modeling, statistical studies, digitalization.",
    },
    cta1: { fr: "Découvrir nos services", en: "Discover our services" },
    cta2: { fr: "Nos réalisations", en: "Our achievements" },
    stats: {
      projects: { fr: "Projets réalisés", en: "Completed projects" },
      staff: { fr: "Effectifs permanents", en: "Permanent staff" },
      partners: { fr: "Partenaires", en: "Partners" },
      experience: { fr: "Années d'expérience", en: "Years of experience" },
      leadership: { fr: "Expérience des dirigeants", en: "Leadership experience" },
    },
  },

  // About
  about: {
    badge: { fr: "Qui sommes-nous", en: "Who we are" },
    title: { fr: "À Propos de", en: "About" },
    titleHighlight: { fr: "Nous", en: "Us" },
    subtitle: {
      fr: "Fondée en 2019, notre société accompagne les entreprises et institutions dans leurs décisions stratégiques les plus importantes.",
      en: "Founded in 2019, our company supports businesses and institutions in their most important strategic decisions.",
    },
    storyBadge: { fr: "Notre Histoire", en: "Our Story" },
    storyTitle: { fr: "Une expertise forgée par", en: "Expertise forged through" },
    storyHighlight: { fr: "l'excellence", en: "excellence" },
    storyP1: {
      fr: "Créée par une économiste et financière, Madame la Directrice Générale Aida Djigo Wane, notre société s'est rapidement imposée comme une référence en matière d'études économiques et financières et en transformation digitale.",
      en: "Founded by an economist and financial expert, CEO Aida Djigo Wane, our company quickly established itself as a reference in economic and financial studies and digital transformation.",
    },
    storyP2: {
      fr: "Nous avons accompagné plus de 15 projets, des PME aux grandes corporations, en passant par les institutions publiques, dans leurs enjeux de croissance, de transformation et d'optimisation.",
      en: "We have supported over 15 projects, from SMEs to large corporations and public institutions, in their growth, transformation and optimization challenges.",
    },
    storyP3: {
      fr: "Notre approche combine rigueur méthodologique, innovation technologique et compréhension fine des enjeux sectoriels.",
      en: "Our approach combines methodological rigor, technological innovation and deep understanding of sector-specific challenges.",
    },
    statsYears: { fr: "Années d'expérience", en: "Years of experience" },
    statsProjects: { fr: "Projets réalisés", en: "Completed projects" },
    statsClients: { fr: "Clients satisfaits", en: "Satisfied clients" },
    imageQuote: {
      fr: "« L'excellence au service de la transformation économique africaine »",
      en: "\"Excellence in service of African economic transformation\"",
    },
    valuesTitle: { fr: "Nos Valeurs", en: "Our Values" },
    valuesSubtitle: { fr: "Les principes qui guident chacune de nos missions", en: "The principles that guide each of our missions" },
    values: {
      excellence: { title: { fr: "Excellence", en: "Excellence" }, desc: { fr: "Les meilleures méthodologies et outils d'analyse.", en: "The best methodologies and analytical tools." } },
      collaboration: { title: { fr: "Collaboration", en: "Collaboration" }, desc: { fr: "Étroite collaboration avec nos clients.", en: "Close collaboration with our clients." } },
      expertise: { title: { fr: "Expertise", en: "Expertise" }, desc: { fr: "Expertise académique et expérience terrain.", en: "Academic expertise and field experience." } },
      innovation: { title: { fr: "Innovation", en: "Innovation" }, desc: { fr: "Technologies et méthodes de pointe.", en: "Cutting-edge technologies and methods." } },
    },
    teamTitle: { fr: "Notre Équipe", en: "Our Team" },
    teamSubtitle: { fr: "Les talents qui font notre force", en: "The talents that make our strength" },
  },

  // Services
  services: {
    badge: { fr: "Ce que nous faisons", en: "What we do" },
    title: { fr: "Services Réalisés", en: "Services Delivered" },
    subtitle: {
      fr: "Notre cabinet a accompagné plusieurs institutions publiques, organisations internationales et entreprises privées dans la structuration financière, l'ingénierie de projets et la mise en place de partenariats public-privé (PPP).",
      en: "Our firm has supported several public institutions, international organizations and private companies in financial structuring, project engineering and public-private partnership (PPP) implementation.",
    },
    ctaTitle: { fr: "Besoin d'une solution sur mesure ?", en: "Need a tailored solution?" },
    ctaDesc: {
      fr: "Chaque entreprise est unique. Contactez-nous pour obtenir une proposition personnalisée.",
      en: "Every business is unique. Contact us for a personalized proposal.",
    },
    ctaButton: { fr: "Demander un devis gratuit", en: "Request a free quote" },
    items: [
      {
        title: { fr: "Structuration et ingénierie financière de projets", en: "Project financial structuring and engineering" },
        description: {
          fr: "Nous avons contribué à la structuration financière et à la modélisation économique de grands projets d'infrastructures en Afrique de l'Ouest et en Afrique centrale, incluant projets autoroutiers, transports urbains (BRT et TER), agropoles et infrastructures économiques en PPP.",
          en: "We have contributed to the financial structuring and economic modeling of major infrastructure projects in West and Central Africa, including highway projects, urban transport (BRT and TER), agropoles and PPP economic infrastructure.",
        },
        features: { fr: ["Modélisation économique", "PPP", "Infrastructures", "Agropoles"], en: ["Economic modeling", "PPP", "Infrastructure", "Agropoles"] },
      },
      {
        title: { fr: "Études économiques et modèles financiers", en: "Economic studies and financial models" },
        description: {
          fr: "Réalisation d'études économiques stratégiques et de modèles financiers pour des gouvernements, institutions internationales et agences de développement, portant sur la croissance verte, le financement agricole et les mécanismes innovants de financement.",
          en: "Conducting strategic economic studies and financial models for governments, international institutions and development agencies, focusing on green growth, agricultural financing and innovative financing mechanisms.",
        },
        features: { fr: ["Croissance verte", "Financement agricole", "Mécanismes innovants", "Études stratégiques"], en: ["Green growth", "Agricultural financing", "Innovative mechanisms", "Strategic studies"] },
      },
      {
        title: { fr: "Développement des infrastructures et des transports", en: "Infrastructure and transport development" },
        description: {
          fr: "Participation à des projets majeurs : structuration financière du BRT de Dakar, projets autoroutiers en PPP, due diligence financière du projet de relance des chemins de fer Abidjan – Ouagadougou – Kaya.",
          en: "Participation in major projects: financial structuring of the Dakar BRT, PPP highway projects, financial due diligence for the Abidjan–Ouagadougou–Kaya railway revival project.",
        },
        features: { fr: ["BRT Dakar", "Autoroutes PPP", "Chemins de fer", "Due diligence"], en: ["Dakar BRT", "PPP Highways", "Railways", "Due diligence"] },
      },
      {
        title: { fr: "Gouvernance économique et promotion des investissements", en: "Economic governance and investment promotion" },
        description: {
          fr: "Accompagnement d'institutions publiques dans l'amélioration de l'environnement des affaires et la promotion des investissements privés, incluant programmes de promotion, guichets uniques et formation institutionnelle.",
          en: "Supporting public institutions in improving the business environment and promoting private investment, including promotion programs, one-stop shops and institutional training.",
        },
        features: { fr: ["Environnement des affaires", "Investissements", "Guichets uniques", "Formation"], en: ["Business environment", "Investment", "One-stop shops", "Training"] },
      },
      {
        title: { fr: "Eau, assainissement et développement durable", en: "Water, sanitation and sustainable development" },
        description: {
          fr: "Missions dans le secteur de l'eau et de l'assainissement : modèles économiques pour la gestion des boues de vidange, mécanismes de financement pour un Fonds Bleu, et actualisation de modèles financiers pour les organismes publics du secteur.",
          en: "Missions in the water and sanitation sector: economic models for fecal sludge management, financing mechanisms for a Blue Fund, and updating financial models for public sector organizations.",
        },
        features: { fr: ["Fonds Bleu", "Boues de vidange", "Modèles financiers", "Développement durable"], en: ["Blue Fund", "Fecal sludge", "Financial models", "Sustainable development"] },
      },
      {
        title: { fr: "Conseil stratégique et financement d'entreprises", en: "Strategic consulting and corporate financing" },
        description: {
          fr: "Accompagnement des entreprises privées dans la structuration financière, l'optimisation des actifs et la levée de fonds sur les marchés financiers.",
          en: "Supporting private companies in financial structuring, asset optimization and fundraising on financial markets.",
        },
        features: { fr: ["Structuration financière", "Optimisation actifs", "Levée de fonds", "Marchés financiers"], en: ["Financial structuring", "Asset optimization", "Fundraising", "Financial markets"] },
      },
    ],
  },

  // Projects
  projects: {
    badge: { fr: "Portfolio", en: "Portfolio" },
    title: { fr: "Projets Réalisés", en: "Completed Projects" },
    subtitle: {
      fr: "Découvrez nos réalisations récentes et les résultats obtenus pour nos clients à travers l'Afrique.",
      en: "Discover our recent achievements and results obtained for our clients across Africa.",
    },
    filters: {
      all: { fr: "Tous", en: "All" },
      marketStudy: { fr: "Études de marché", en: "Market studies" },
      financialAnalysis: { fr: "Analyses financières", en: "Financial analysis" },
      strategicConsulting: { fr: "Conseil stratégique", en: "Strategic consulting" },
    },
    typeLabels: {
      "market-study": { fr: "Étude de marché", en: "Market study" },
      "financial-analysis": { fr: "Analyse financière", en: "Financial analysis" },
      "strategic-consulting": { fr: "Conseil stratégique", en: "Strategic consulting" },
    },
    showing: { fr: "Affichage de", en: "Showing" },
    projectWord: { fr: "projet", en: "project" },
    on: { fr: "sur", en: "of" },
  },

  // Partners
  partners: {
    badge: { fr: "Confiance", en: "Trust" },
    title: { fr: "Ils nous font confiance", en: "They trust us" },
    subtitle: {
      fr: "Nous collaborons avec des institutions de renom et des entreprises leaders.",
      en: "We collaborate with renowned institutions and leading companies.",
    },
    ctaTitle: { fr: "Rejoignez notre réseau", en: "Join our network" },
    ctaDesc: { fr: "Explorons ensemble les opportunités de collaboration.", en: "Let's explore collaboration opportunities together." },
    ctaButton: { fr: "Devenir partenaire", en: "Become a partner" },
  },

  // Contact
  contact: {
    badge: { fr: "Parlons de votre projet", en: "Let's talk about your project" },
    title: { fr: "Contactez-nous", en: "Contact us" },
    subtitle: {
      fr: "Discutons de vos besoins et obtenez une proposition personnalisée.",
      en: "Let's discuss your needs and get a personalized proposal.",
    },
    address: { fr: "Adresse", en: "Address" },
    phone: { fr: "Téléphone", en: "Phone" },
    email: { fr: "Email", en: "Email" },
    hours: { fr: "Horaires", en: "Hours" },
    hoursDetails: { fr: "Lun - Ven : 8h00 - 17h00", en: "Mon - Fri: 8:00 AM - 5:00 PM" },
    formTitle: { fr: "Envoyez-nous un message", en: "Send us a message" },
    nameLabel: { fr: "Nom complet *", en: "Full name *" },
    namePlaceholder: { fr: "Votre nom", en: "Your name" },
    emailLabel: { fr: "Email *", en: "Email *" },
    subjectLabel: { fr: "Sujet *", en: "Subject *" },
    subjectPlaceholder: { fr: "Sujet de votre demande", en: "Subject of your request" },
    messageLabel: { fr: "Message *", en: "Message *" },
    messagePlaceholder: { fr: "Décrivez votre projet...", en: "Describe your project..." },
    sending: { fr: "Envoi en cours...", en: "Sending..." },
    send: { fr: "Envoyer le message", en: "Send message" },
    successTitle: { fr: "Message envoyé !", en: "Message sent!" },
    successDesc: { fr: "Nous vous recontacterons dans les plus brefs délais.", en: "We will get back to you as soon as possible." },
    errorTitle: { fr: "Erreur", en: "Error" },
    errorDesc: { fr: "Une erreur est survenue. Veuillez réessayer.", en: "An error occurred. Please try again." },
  },

  // Footer
  footer: {
    subtitle: { fr: "Études & Conseil Économique", en: "Economic Studies & Consulting" },
    description: {
      fr: "Votre partenaire de confiance pour toutes vos analyses financières et études économiques depuis 2019.",
      en: "Your trusted partner for all your financial analyses and economic studies since 2019.",
    },
    servicesTitle: { fr: "Services", en: "Services" },
    services: {
      fr: ["Études de marché", "Analyses financières", "Conseil stratégique", "Études sectorielles", "Accompagnement PME", "Développement digital"],
      en: ["Market studies", "Financial analysis", "Strategic consulting", "Sector studies", "SME support", "Digital development"],
    },
    companyTitle: { fr: "Entreprise", en: "Company" },
    company: {
      fr: ["À propos", "Notre équipe", "Nos valeurs", "Carrières", "Actualités", "Contact"],
      en: ["About", "Our team", "Our values", "Careers", "News", "Contact"],
    },
    followTitle: { fr: "Suivez-nous", en: "Follow us" },
    followDesc: {
      fr: "Restez informé de nos analyses économiques et actualités.",
      en: "Stay informed about our economic analyses and news.",
    },
    rights: { fr: "Tous droits réservés.", en: "All rights reserved." },
    legal: {
      fr: ["Mentions légales", "Politique de confidentialité", "Conditions d'utilisation", "RGPD"],
      en: ["Legal notice", "Privacy policy", "Terms of use", "GDPR"],
    },
  },
} as const;

export type TranslationKey = keyof typeof translations;

export function t(obj: { fr: string; en: string }, lang: Language): string {
  return obj[lang];
}
